<template>
  <div class="rms-t4-reviews min-w-0">
    <h3 v-if="showHeading" id="reviews-heading-t4" class="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-6">
      {{ $t('public.reviewsAndFeedback') }}
    </h3>
    <template v-if="feedbacks.length">
      <!-- Single feedback -->
      <div v-if="feedbacks.length <= 1" class="relative">
        <div
          class="p-6 sm:p-8 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md min-h-[200px] flex flex-col"
          role="article"
        >
          <div class="flex gap-1.5 mb-4" aria-hidden="true">
            <span
              v-for="s in 5"
              :key="s"
              class="material-icons text-2xl"
              :class="s > starCount(feedbacks[0]) ? 'text-slate-400 dark:text-slate-600' : ''"
              :style="s <= starCount(feedbacks[0]) ? starFilledStyle : undefined"
            >star</span>
          </div>
          <p class="text-slate-800 dark:text-slate-200 font-medium leading-relaxed mb-6 flex-1">"{{ feedbacks[0].text || '' }}"</p>
          <div class="flex items-center gap-3">
            <div
              class="w-11 h-11 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-600"
              aria-hidden="true"
            >
              <span class="text-sm font-semibold tracking-tight text-slate-600 dark:text-slate-300 select-none">{{ feedbackInitials(feedbacks[0].name) }}</span>
            </div>
            <div>
              <p class="font-bold text-slate-900 dark:text-slate-100">{{ feedbacks[0].name || 'Anonymous' }}</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Multiple: Swiper -->
      <div v-else class="relative">
        <Swiper
          :modules="swiperModules"
          effect="fade"
          :fade-effect="{ crossFade: true }"
          :slides-per-view="1"
          :space-between="0"
          :speed="reducedMotion ? 0 : 350"
          :autoplay="autoplayConfig"
          :pagination="paginationConfig"
          :a11y="a11yConfig"
          class="rms-t4-reviews-swiper min-h-[280px]"
          @swiper="onSwiper"
        >
          <SwiperSlide v-for="(fb, idx) in feedbacks" :key="idx">
            <div
              class="p-6 sm:p-8 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md w-full min-h-[280px] flex flex-col"
              role="article"
            >
              <div class="flex gap-1.5 mb-4" aria-hidden="true">
                <span
                  v-for="s in 5"
                  :key="s"
                  class="material-icons text-2xl"
                  :class="s > starCount(fb) ? 'text-slate-400 dark:text-slate-600' : ''"
                  :style="s <= starCount(fb) ? starFilledStyle : undefined"
                >star</span>
              </div>
              <p class="text-slate-800 dark:text-slate-200 font-medium leading-relaxed mb-6 flex-1">"{{ fb.text || '' }}"</p>
              <div class="flex items-center gap-3">
                <div
                  class="w-11 h-11 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-600"
                  aria-hidden="true"
                >
                  <span class="text-sm font-semibold tracking-tight text-slate-600 dark:text-slate-300 select-none">{{ feedbackInitials(fb.name) }}</span>
                </div>
                <div>
                  <p class="font-bold text-slate-900 dark:text-slate-100">{{ fb.name || 'Anonymous' }}</p>
                </div>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </template>
    <div v-else class="py-10 px-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
      <span class="material-symbols-outlined text-4xl text-slate-300 dark:text-slate-600 mb-3 block" aria-hidden="true">rate_review</span>
      <p class="text-slate-600 dark:text-slate-400">{{ emptyMessage }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectFade, Autoplay, Pagination, A11y } from 'swiper/modules'
import { useReducedMotion } from '@/composables/useReducedMotion'
import { feedbackInitials } from '@/utils/feedbackInitials'
import 'swiper/css'
import 'swiper/css/effect-fade'
import 'swiper/css/pagination'

const props = defineProps({
  feedbacks: { type: Array, default: () => [] },
  emptyMessage: { type: String, default: 'No reviews yet.' },
  /** When false, parent provides the section heading (unified reviews + form layout). */
  showHeading: { type: Boolean, default: true },
})

const reducedMotion = useReducedMotion()

const swiperModules = [EffectFade, Autoplay, Pagination, A11y]

/** Filled stars: solid Material Icons "star" with primary color. */
const starFilledStyle = computed(() => ({
  color: 'var(--rms-t4-primary, #ec5b13)',
}))

const autoplayConfig = computed(() => {
  if (reducedMotion.value || props.feedbacks.length <= 1) return false
  return { delay: 5000, disableOnInteraction: false }
})

const paginationConfig = computed(() => {
  if (props.feedbacks.length <= 1) return false
  return {
    clickable: true,
    bulletClass: 'rms-t4-reviews__dot',
    bulletActiveClass: 'rms-t4-reviews__dot--active',
  }
})

const a11yConfig = computed(() => ({
  paginationBulletMessage: `Review {{index}} of ${props.feedbacks.length}`,
}))

function starCount(fb) {
  const r = Number(fb?.rating)
  return Math.min(5, Math.max(0, Number.isNaN(r) ? 0 : Math.round(r)))
}

function onSwiper(swiper) {
  if (swiper.pagination?.el) {
    swiper.pagination.el.setAttribute('role', 'tablist')
    swiper.pagination.el.setAttribute('aria-label', 'Review slides')
  }
  function setBulletA11y() {
    if (!swiper.pagination?.bullets) return
    swiper.pagination.bullets.forEach((bullet, i) => {
      bullet.setAttribute('role', 'tab')
      bullet.setAttribute('aria-selected', i === swiper.activeIndex ? 'true' : 'false')
    })
  }
  setBulletA11y()
  swiper.on('slideChange', setBulletA11y)
}
</script>

<style scoped>
.rms-t4-reviews-swiper :deep(.swiper-pagination) {
  position: relative;
  margin-top: 1rem;
}
.rms-t4-reviews-swiper :deep(.rms-t4-reviews__dot) {
  width: 10px;
  height: 10px;
  background: #cbd5e1;
  opacity: 0.7;
  border-radius: 9999px;
  display: inline-block;
  margin: 0 5px;
  cursor: pointer;
  transition: background-color 0.2s, opacity 0.2s;
}
.rms-t4-reviews-swiper :deep(.rms-t4-reviews__dot:hover),
.rms-t4-reviews-swiper :deep(.rms-t4-reviews__dot--active) {
  opacity: 1;
  background: var(--rms-t4-primary, #ec5b13);
}
</style>
